import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, AlertTriangle, ClipboardCheck, ChevronRight } from "lucide-react";

const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

function ScaleInput({ value, onChange, id }) {
  return (
    <div className="flex gap-1 flex-wrap" data-testid={`scale-${id}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-sm border text-sm font-semibold transition-colors ${
            value === n ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
          }`}
          data-testid={`scale-${id}-${n}`}
        >
          {n}
        </button>
      ))}
      <span className="ml-2 self-center text-[11px] text-muted-foreground">1 = muito baixo · 5 = excelente</span>
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  if (q.type === "text") {
    return (
      <textarea
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
        data-testid={`q-${q.id}`}
      />
    );
  }
  if (q.type === "number") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="number" step="0.1"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-32 px-3 py-2 rounded-sm border border-input bg-card text-sm"
          data-testid={`q-${q.id}`}
        />
        {q.unit && <span className="text-xs text-muted-foreground">{q.unit}</span>}
      </div>
    );
  }
  if (q.type === "scale5") {
    return <ScaleInput value={value} onChange={onChange} id={q.id} />;
  }
  if (q.type === "single") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>
        {q.options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
              value === opt ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
            data-testid={`q-${q.id}-opt-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt];
      onChange(next);
    };
    return (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>
        {q.options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
              arr.includes(opt) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  return null;
}

export default function PublicMetabolicTestPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, test: null });
  const [answers, setAnswers] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/metabolic-test/${token}`);
        setState({ loading: false, error: null, test: data });
        if (data.already_submitted) setDone(true);
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Link inválido.", test: null });
      }
    })();
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground overline">Carregando…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="bg-card border border-destructive/30 rounded-sm p-6 max-w-md text-center">
          <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-3" />
          <h2 className="brand-wordmark uppercase text-lg font-bold mb-2">Link inválido</h2>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  const test = state.test;

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center p-6" data-testid="metabolic-done">
        <div className="bg-card border border-emerald-500/30 rounded-sm p-8 max-w-md text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
          <h2 className="brand-wordmark uppercase text-xl font-bold text-primary mb-2">Respostas enviadas</h2>
          <p className="text-sm text-muted-foreground">
            Obrigado! Suas respostas foram registradas e estarão disponíveis para o profissional na sua próxima consulta.
          </p>
        </div>
      </div>
    );
  }

  const sections = test.questionnaire;
  const currentSection = sections[sectionIdx];
  const isLast = sectionIdx === sections.length - 1;

  const setAnswer = (qid, v) => setAnswers((prev) => ({ ...prev, [qid]: v }));

  const canProceed = () => {
    return currentSection.questions
      .filter((q) => q.required)
      .every((q) => {
        const v = answers[q.id];
        return v !== undefined && v !== "" && v !== null;
      });
  };

  const next = () => {
    if (!canProceed()) { toast.error("Responda todas as perguntas obrigatórias."); return; }
    setSectionIdx((i) => i + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setSectionIdx((i) => Math.max(0, i - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    if (!canProceed()) { toast.error("Responda todas as perguntas obrigatórias."); return; }
    setSubmitting(true);
    try {
      await publicApi.post(`/public/metabolic-test/${token}/submit`, { answers });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar respostas."); }
    setSubmitting(false);
  };

  const pct = Math.round(((sectionIdx + 1) / sections.length) * 100);

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="metabolic-public-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="overline text-gold mb-2 inline-flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Pré-consulta
          </div>
          <h1 className="brand-wordmark text-2xl md:text-3xl font-bold tracking-tight uppercase text-primary">Teste de equilíbrio metabólico</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {test.patient_name ? `Olá, ${test.patient_name}! ` : ""}
            Suas respostas ajudam o profissional a preparar sua consulta com mais precisão. Leva ~5 minutos.
          </p>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-1 text-xs text-muted-foreground">
            <span>Seção {sectionIdx + 1} de {sections.length}</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-sm overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-5 md:p-6">
          <div className="overline text-gold mb-4">{currentSection.title}</div>
          <div className="space-y-5">
            {currentSection.questions.map((q) => (
              <div key={q.id}>
                <label className="block text-sm font-medium mb-2">
                  {q.label} {q.required && <span className="text-destructive">*</span>}
                </label>
                <QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <button
            onClick={back} disabled={sectionIdx === 0}
            className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="mt-back-btn"
          >
            Voltar
          </button>
          {!isLast ? (
            <button
              onClick={next}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
              data-testid="mt-next-btn"
            >
              Próximo <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit} disabled={submitting}
              className="px-6 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
              data-testid="mt-submit-btn"
            >
              {submitting ? "Enviando…" : "Enviar respostas"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
